/**
 * **The line panel: your fab, its readings, what the last move bought, and
 * the levers** (B0.7's scoreboard, restated at M1.1 as the measured line's
 * info window — docs/11-interface.md).
 *
 * B0.5's Site & flow panel explains *what the floor is doing*. This one exists
 * to answer the question that makes explaining it worth anything: **is that
 * good, and what should I do about it?** In the campaign the answer is the
 * economy's, not a brief's — so the B0.7 objective and its met/missed verdict
 * are gone, and the yardstick is the **rented line**: someone else's fab, hired
 * by the wafer, whose price per wafer is what your own has to beat. Built in
 * the order a player uses it:
 *
 * 1. **The readings.** Wafers a week, cycle time, cost per wafer — the watched
 *    number beside the curve's forecast, and *settling* while the floor is
 *    still rearranging after a change.
 * 2. **What the last move bought.** D0's lesson, carried across a prototype: *a
 *    visible delta is what makes a change feel like a move*.
 * 3. **The levers.** Release rate, shift, dispatch rule, and the two that live
 *    on the canvas (move a machine, expedite a lot). Everything here is a
 *    command; nothing here is a call.
 *
 * The cost breakdown is not decoration. Cost per wafer is the number a player is
 * asked to *act* on, and a number you cannot take apart is a number nobody can
 * act on — so the lines it is made of are one click away, priced by the sim
 * and never re-derived here. On-demand: opened from the Dashboard's line row
 * and the dock, one of the overlays over the live floor.
 */

import { useState, useSyncExternalStore, type ReactNode } from 'react';
import type { CalibrationUi, Dispatch, Figure, FloorHost, LineUi, MoveUi, ScoreUi } from '../bridge';
import { buildState, subscribeBuild } from './build';
import { openHandbook, setPanels, usePanels } from './panels';
import { snapshot, subscribe, type HudState } from './store';
import { Tip, atomic } from './Tooltip';
import { Calibration, Machines, type FlyTo } from './SiteFlow';
import { money } from './format';

export function LinePanel({ host, flyTo }: { host: FloorHost; flyTo: FlyTo }) {
  const hud = useSyncExternalStore(subscribe, snapshot);
  const build = useSyncExternalStore(subscribeBuild, buildState);
  const panels = usePanels();
  const ui = hud.ui;
  const c = hud.campaign;
  if (panels.economy !== 'line' || !ui) return null;
  const score = ui.score;
  const site = c?.siteLine != null ? c.firm.lines[c.siteLine] : undefined;
  const rented = c?.firm.lines.find((l) => l.source === 'rented');

  return (
    <div className="overlay scoreboard">
      <header>
        <h2>{site ? site.name : 'The floor'}</h2>
        {site && (
          <span className="chip">
            {site.sourceLabel} · {site.nodeName}
            {site.product ? ` · running ${site.product}` : ' · idle'}
          </span>
        )}
        {c?.siteCurveStale && <span className="chip warn">curve stale — the floor changed and has not re-measured</span>}
        <button className="help" onClick={() => openHandbook('lines')} title="the handbook: lines">
          ?
        </button>
        <button className="close" onClick={() => setPanels({ economy: null })}>
          ×
        </button>
      </header>
      <Readings score={score} forecast={ui.calibration} rented={rented} />
      <Cost score={score} rented={rented} site={site} />
      <Delta moves={score.moves} />
      <Levers host={host} state={hud} moving={build.mode.kind === 'relocate'} />
      <Fold title="Calibration & curve" hint={ui.calibration.ready ? `${ui.calibration.windows} windows · capacity ${ui.calibration.capacityWafersPerWeek.toLocaleString()} wafers/wk` : 'nothing measured yet'}>
        <Calibration host={host} flyTo={flyTo} />
      </Fold>
      <Fold title="Machines" hint={`${ui.flow.tools.length} installed · constraint ${score.bottleneck || 'none'}`}>
        <Machines host={host} flyTo={flyTo} />
      </Fold>
    </div>
  );
}

/** A collapsible section, folded by default: the diagnostics under the readings. */
function Fold({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="fold">
      <button className="fold-head" onClick={() => setOpen(!open)}>
        <span className="chev">{open ? '▾' : '▸'}</span>
        <span className="fold-title">{title}</span>
        {hint && <span className="fold-hint">{hint}</span>}
      </button>
      {open ? <div className="fold-body">{children}</div> : null}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * The three numbers
 * ------------------------------------------------------------------ */

/**
 * The three readings, each the watched number beside the curve's forecast for
 * this loading — the seam on screen, as Site & flow shows it — and each a
 * tooltip figure so the term is one click from the handbook.
 */
function Readings({
  score,
  forecast,
  rented,
}: {
  score: ScoreUi;
  forecast: CalibrationUi;
  rented: LineUi | undefined;
}) {
  const rows: { label: string; value: string; forecast: string; figure: Figure }[] = [
    {
      label: 'out',
      value: `${score.wafersPerWeek.toLocaleString()} wafers/wk`,
      forecast: forecast.forecast ? `${forecast.forecastWafersPerWeek.toLocaleString()}` : '—',
      figure: atomic(
        'Wafers out a week',
        `${score.wafersPerWeek.toLocaleString()} wafers/wk`,
        'The merged curve point at the current release dial — what the floor has been measured to ship, never one window. Beside it, what the curve alone forecasts.',
      ),
    },
    {
      label: 'cycle',
      value: score.cycleHours > 0 ? `${score.cycleHours.toFixed(1)} h` : '—',
      forecast: forecast.forecast ? `${forecast.forecastCycleHours.toFixed(1)} h` : '—',
      figure: atomic(
        'Cycle time',
        score.cycleHours > 0 ? `${score.cycleHours.toFixed(1)} h door to door` : '—',
        'Mean door-to-door time of the lots that finished. Past capacity it is a lower bound, not a prediction.',
      ),
    },
    {
      label: 'cost',
      value: score.costPerWaferDollars > 0 ? `$${score.costPerWaferDollars.toFixed(2)}/wafer` : '—',
      forecast: rented?.costPerWafer != null ? `rented: $${rented.costPerWafer.toFixed(2)}` : '—',
      figure: atomic(
        'Cost per wafer',
        score.costPerWaferDollars > 0 ? `$${score.costPerWaferDollars.toFixed(2)}` : '—',
        rented?.costPerWafer != null
          ? `The site's weekly cost stack over its wafers out. The yardstick is the rented line — ${rented.name} — at $${rented.costPerWafer.toFixed(2)} a wafer at its dial.`
          : "The site's weekly cost stack over its wafers out.",
      ),
    },
  ];
  return (
    <section className="meters">
      {!score.settled ? (
        <p className="settling">
          settling after a change — these are the floor rearranging itself, not what it will do
        </p>
      ) : null}
      <div className="row readings-head">
        <span className="label" />
        <span className="value muted">watched</span>
        <span className="target-value muted">forecast / yardstick</span>
      </div>
      {rows.map((row) => (
        <div className="row reading" key={row.label}>
          <span className="label">{row.label}</span>
          <span className="value">
            <Tip figure={row.figure}>{row.value}</Tip>
          </span>
          <span className="target-value">{row.forecast}</span>
        </div>
      ))}
      <div className="row">
        <span className="label">constraint</span>
        <span className="value">{score.bottleneck || 'nothing installed'}</span>
      </div>
    </section>
  );
}

/**
 * What the week costs, and therefore what a wafer costs. Collapsed by default
 * and one click from open: the total is what you watch, the four lines are what
 * you act on when it will not come down.
 */
function Cost({ score, rented, site }: { score: ScoreUi; rented: LineUi | undefined; site: LineUi | undefined }) {
  const [open, setOpen] = useState(false);
  const cost = score.cost;
  const share = (line: number) => (cost.totalDollars > 0 ? (line / cost.totalDollars) * 100 : 0);
  const lines: [string, number, string][] = [
    [
      'upkeep',
      cost.upkeepDollars,
      `rooms $${Math.round(cost.upkeepRoomsDollars).toLocaleString()} (clean space by the square metre) + machines $${Math.round(
        cost.upkeepMachinesDollars,
      ).toLocaleString()} as each is being kept — neglect is cheaper and wears faster`,
    ],
    ['wages', cost.wagesDollars, 'the shift, at the wages the era pays — on the ledger as the line\'s fixed cost'],
    ['materials', cost.materialsDollars, 'silicon and chemistry, charged on wafers started'],
    [
      'depreciation',
      cost.depreciationDollars,
      score.objective
        ? `$${Math.round(cost.capitalDollars).toLocaleString()} of capital — rooms $${Math.round(
            cost.capitalRoomsDollars,
          ).toLocaleString()} at list, machines $${Math.round(
            cost.capitalMachinesDollars,
          ).toLocaleString()} at what was paid plus overhauls — written off over ${
            score.objective.capitalWeeks
          } weeks. A view of what was spent, not cash: the ledger paid when it was bought`
        : 'capital written off — a view, not cash',
    ],
  ];
  return (
    <section className="cost">
      <button className="cost-head" onClick={() => setOpen(!open)}>
        <span className="label">a week costs</span>
        <span className="value">${Math.round(cost.totalDollars).toLocaleString()}</span>
        <span className="chev">{open ? '▾' : '▸'}</span>
      </button>
      {open ? (
        <>
          {lines.map(([label, value, note]) => (
            <div className="cost-row" key={label}>
              <span className="label" title={note}>
                {label}
              </span>
              <span className="meter">
                <span style={{ width: `${share(value)}%` }} />
              </span>
              <span className="value">${Math.round(value).toLocaleString()}</span>
            </div>
          ))}
          <p className="note">
            {score.waferStartsPerWeek.toLocaleString()} wafers started a week against{' '}
            {score.wafersPerWeek.toLocaleString()} out — silicon is bought at the release, so
            running the floor past what it can ship is paid for twice.
          </p>
          {rented && (
            <p className="note">
              The rented line — {rented.name} — costs {money(rented.weeklyCost)} a week for {rented.wafersOut} wafers
              {rented.costPerWafer != null ? ` ($${rented.costPerWafer.toFixed(2)} a wafer)` : ''} at its dial;
              {site ? ` this fab's costs reach the ledger as ${site.name}'s line.` : ''}
            </p>
          )}
        </>
      ) : null}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Since your last change
 * ------------------------------------------------------------------ */

/**
 * **What the last move bought**, as one sentence rather than a log.
 *
 * Three refusals still govern what the sentence may say (B0.7): a move whose
 * window has not closed reads *settling*; a move another lever landed on top
 * of has no delta to claim; and a move with nothing measured before it is a
 * reading, not a difference. A bottleneck handed over is named, because that
 * is the thing the whole loop is about. The full move history is the playtest
 * log's, not the panel's.
 */
function Delta({ moves }: { moves: MoveUi[] }) {
  const move = moves[0];
  if (!move) return null;
  const after = move.after;
  let body: ReactNode;
  if (!after) {
    body = (
      <span className="settling">
        {move.superseded ? 'another lever moved before this one had a reading' : 'settling — the floor is still rearranging'}
      </span>
    );
  } else if (!move.comparable) {
    body = (
      <span className="note">
        first reading: {after.wafersPerWeek.toLocaleString()} wafers/wk · {after.cycleHours.toFixed(1)} h
        {after.costPerWaferDollars > 0 ? ` · $${after.costPerWaferDollars.toFixed(2)}/wafer` : ''}
      </span>
    );
  } else {
    body = (
      <span className="deltas">
        <Change delta={move.deltaWafers} format={(v) => `${v > 0 ? '+' : ''}${v} wafers/wk`} good={(v) => v > 0} />
        <Change delta={move.deltaCycleHours} format={(v) => `${v > 0 ? '+' : ''}${v.toFixed(1)} h`} good={(v) => v < 0} />
        <Change delta={move.deltaCostDollars} format={(v) => `${v > 0 ? '+' : ''}$${v.toFixed(2)}/wafer`} good={(v) => v < 0} />
        {move.before.bottleneck !== after.bottleneck ? (
          <span className="handover">
            {move.before.bottleneck || 'nothing'} → {after.bottleneck || 'nothing'}
          </span>
        ) : null}
        {move.fixedABottleneck ? <span className="chip met">bottleneck fixed</span> : null}
        {move.paired ? (
          <span
            className="chip ok"
            title="the re-measurement replayed the same arrival and breakdown dice the before-reading rolled — the delta is the change's effect, not the dice's"
          >
            paired
          </span>
        ) : null}
      </span>
    );
  }
  return (
    <section className="delta">
      <div className="row">
        <span className="label">
          since {move.label.toLowerCase()}
          {move.note ? <span className="note"> · {move.note}</span> : null}
        </span>
        <span className="value">{body}</span>
      </div>
    </section>
  );
}

function Change({
  delta,
  format,
  good,
}: {
  delta: number | null;
  format: (value: number) => string;
  good: (value: number) => boolean;
}) {
  // `== null`, not `=== null`: an absent `Option` crosses the wasm boundary as
  // `undefined` rather than `null` (serde-wasm-bindgen), whatever the generated
  // type says. Nullish checks are the house rule on this side because of it.
  if (delta == null) return <span className="change">—</span>;
  const tone = delta === 0 ? '' : good(delta) ? 'good' : 'bad';
  return <span className={`change ${tone}`}>{format(delta)}</span>;
}

/* ------------------------------------------------------------------ *
 * The levers
 * ------------------------------------------------------------------ */

/**
 * The floor's dials, gathered: the release rate, the shift, the dispatch
 * rule. Each is a real control rather than a preset ladder — the ladders were
 * B0.6's boundary rungs, not staffing choices — and each is a command; nothing
 * here is a call. Move and demolish live in the dock; expedite is shift-click.
 */
function Levers({ host, state, moving }: { host: FloorHost; state: HudState; moving: boolean }) {
  const ui = state.ui;
  const catalog = state.catalog;
  if (!ui) return null;
  const slots = ui.flow.slots;
  const crew = ui.agents;
  const wages = ui.score.cost.wagesDollars;
  const shifts = ui.crew?.shifts ?? 3;
  return (
    <section className="levers">
      <div className="lever">
        <span className="label">release</span>
        {slots.map((slot, i) => (
          <span className="dial" key={i} title={slots.length > 1 ? `slot ${i + 1} — feeds the measured line's product slot ${i + 1}` : undefined}>
            {slots.length > 1 && <span className="muted">s{i + 1} </span>}
            <button
              onClick={() => host.send({ kind: 'setSlotStarts', slot: i, lotsPerDay: Math.max(0, slot.startsPerDay - 1) })}
              title="one lot a day fewer"
            >
              −
            </button>
            <span className="dial-value">{slot.startsPerDay}</span>
            <button
              onClick={() => host.send({ kind: 'setSlotStarts', slot: i, lotsPerDay: slot.startsPerDay + 1 })}
              title="one lot a day more"
            >
              +
            </button>
          </span>
        ))}
        {slots.length < 2 && (
          <button
            className="small"
            title="open a second release slot — the second product on this floor gets its own dial (assign it on the Dashboard)"
            onClick={() => host.send({ kind: 'setSlotStarts', slot: slots.length, lotsPerDay: 1 })}
          >
            + slot
          </button>
        )}
        <span className="unit" title="the curve's x-axis; the measured line's dials on the Dashboard follow the slots">
          lots/day{slots.length > 1 ? ` · ${ui.flow.startsPerDay} total` : ''}
        </span>
      </div>
      <div className="lever">
        <span className="label">shifts</span>
        <span className="dial">
          {[1, 2, 3].map((n) => (
            <button
              key={n}
              className={shifts === n ? 'active' : ''}
              title={
                n === 3
                  ? 'around the clock — full wages, the night shift cuts more corners in the airlock'
                  : `${n * 8} staffed hours a day — ${n === 1 ? 'a third' : 'two thirds'} of the wage bill, and the machines stand idle the rest`
              }
              onClick={() => host.send({ kind: 'setShifts', shifts: n })}
            >
              {n}
            </button>
          ))}
        </span>
        <span className="unit" title="the floor works staffed hours only; the pattern is in the calibration signature">
          × 8 h {ui.crew?.offDuty ? '· clocked off' : ''}
        </span>
      </div>
      <div className="lever">
        <span className="label">shift</span>
        <span className="dial">
          <button onClick={() => host.send({ kind: 'setCrew', count: Math.max(1, crew - 1) })} title="one fewer on shift">
            −
          </button>
          <span className="dial-value">{crew}</span>
          <button onClick={() => host.send({ kind: 'setCrew', count: Math.min(ui.capacity, crew + 1) })} title="one more on shift">
            +
          </button>
          <button onClick={() => host.send({ kind: 'setCrew', count: Math.max(1, crew - 5) })} title="five fewer">
            −5
          </button>
          <button onClick={() => host.send({ kind: 'setCrew', count: Math.min(ui.capacity, crew + 5) })} title="five more">
            +5
          </button>
        </span>
        <span className="unit">
          on the floor · {money(wages)}/wk in wages
          {ui.crew ? ` · discipline ${(ui.crew.disciplineMille / 10).toFixed(0)}%` : ''}
        </span>
      </div>
      <div className="lever">
        <span className="label">dispatch</span>
        {(catalog?.dispatch ?? []).map((rule) => (
          <button
            key={rule.id}
            className={ui.score.dispatch === rule.id ? 'active' : ''}
            title={rule.label}
            onClick={() => host.send({ kind: 'setDispatch', rule: rule.id as Dispatch })}
          >
            {rule.label}
          </button>
        ))}
      </div>
      <p className="hint">
        {moving ? 'click a machine, then click where it should stand · r turns it' : 'move and demolish are in the dock · shift-click a lot to expedite it'}
      </p>
    </section>
  );
}
